// ─────────────────────────────────────────────────────────────────────────────
// Once deployed to Firebase Hosting, this fetches the reserved
// /__/firebase/init.json — every Hosting site serves its own project's
// config there — and initializes with it explicitly. So the exact same
// build deploys to every buyer's own Firebase project with no per-buyer
// edit or rebuild needed.
//
// This used to call initializeApp() with no arguments and let the SDK
// auto-detect the config itself, which is Firebase's documented mechanism
// for this — but empirically, on freshly-provisioned self-serve buyer
// projects, that auto-detection throws `app/no-options` even though
// /__/firebase/init.json itself serves correct data instantly (curl-
// verified). Older, already-established projects (this app's own
// production instance) don't hit it. Rather than depend on whatever
// undocumented signal the SDK's auto-detect actually keys off, fetching
// the same reserved URL ourselves and passing the config explicitly
// sidesteps it entirely.
//
// `npm start` has no Hosting server to fetch from, so local dev needs an
// explicit fallback — set these in a .env.local file (gitignored; copy
// .env.local.example and fill in a real project's config):
//   REACT_APP_DEV_FIREBASE_API_KEY, _AUTH_DOMAIN, _PROJECT_ID,
//   _STORAGE_BUCKET, _MESSAGING_SENDER_ID, _APP_ID
// ─────────────────────────────────────────────────────────────────────────────

import { initializeApp } from "firebase/app";
import { getFirestore }  from "firebase/firestore";
import { getStorage }    from "firebase/storage";
import { getAuth, signInAnonymously } from "firebase/auth";

const devConfig = process.env.NODE_ENV === "development" ? {
  apiKey:            process.env.REACT_APP_DEV_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_DEV_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_DEV_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_DEV_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_DEV_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_DEV_FIREBASE_APP_ID,
} : null;

if (devConfig && !devConfig.apiKey) {
  console.warn(
    "Running `npm start` with no .env.local Firebase config — copy " +
    ".env.local.example to .env.local and fill in a real project's config " +
    "(local dev has no Firebase Hosting server to fetch config from)."
  );
}

export let db, storage, auth;

// index.js awaits this before rendering <App />; App.jsx's existing
// `import { db, storage } from "./firebase"` picks up the values below via
// live ES module bindings once this resolves — no changes needed there.
export async function initFirebase() {
  const config = devConfig?.apiKey ? devConfig : await fetch("/__/firebase/init.json").then((r) => r.json());
  const app = initializeApp(config);
  db      = getFirestore(app);
  storage = getStorage(app);
  auth    = getAuth(app);
  // Firestore/Storage rules require request.auth != null — this is a silent
  // anonymous session (no sign-in UI, PIN gating is unchanged), just enough
  // to stop unauthenticated REST/curl access to a buyer's raw database.
  // A real admin later signing in via signInWithEmailAndPassword (see
  // LoginScreen in App.jsx) replaces this anonymous session on the same
  // `auth` instance — exported here so that call site can reuse it.
  await signInAnonymously(auth);
}
